import { useState, useEffect, useRef } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import AccountSelect from "./AccountSelect";
import MoneyInput from "./MoneyInput";

export default function InputRow({ row, onChange, onDelete, cuentas }) {
  const [local, setLocal] = useState(row);
  const [confirming, setConfirming] = useState(false);
  const dirty = useRef(false);
  const localRef = useRef(local);
  localRef.current = local;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from parent when row changes externally (e.g. ID replacement)
  useEffect(() => {
    if (row.id !== local.id) {
      setLocal(prev => ({ ...prev, id: row.id }));
    }
    if (!dirty.current) setLocal(row);
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  // Force-commit on unmount (React navigation) and on browser refresh (beforeunload)
  useEffect(() => {
    const forceCommit = () => {
      if (dirty.current) {
        dirty.current = false;
        onChangeRef.current(localRef.current);
      }
    };
    window.addEventListener("beforeunload", forceCommit);
    return () => {
      forceCommit();
      window.removeEventListener("beforeunload", forceCommit);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (next) => { dirty.current = true; setLocal(next); };

  const commit = () => {
    if (!dirty.current) return;
    dirty.current = false;
    onChange(local);
  };

  const handleKey = (e) => { if (e.key === "Enter") e.target.blur(); /* blur triggers commit */ };

  // For selects/immediate actions: commit without dirty flag
  const editAndCommit = (next) => { dirty.current = false; onChange(next); setLocal(next); };

  const todayStr = new Date().toISOString().slice(0, 10);
  const isDirty = local.concepto !== row.concepto || local.monto !== row.monto || local.cuentaId !== row.cuentaId || local.fecha !== row.fecha;

  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${P.border}`, opacity: row.esRecurrente ? 0.75 : 1 }}>
      {/* Línea 1: concepto + acciones */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {row.esRecurrente && <span style={{ fontSize: 12 }}>🔁</span>}
        <input
          aria-label="Concepto"
          value={local.concepto}
          onChange={(e) => edit({ ...local, concepto: e.target.value })}
          onBlur={commit}
          onKeyDown={handleKey}
          placeholder="Descripción…"
          style={{ flex: 1, border: "none", background: "transparent", color: P.text, fontSize: 14, fontFamily: FF, outline: "none", padding: "2px 0", fontWeight: 500, minWidth: 0 }}
        />
        <div style={{ position: "relative", width: 110, flexShrink: 0 }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: P.textXs, fontSize: 12, pointerEvents: "none" }}>$</span>
          <MoneyInput
            aria-label="Monto"
            value={local.monto}
            onChange={(v) => edit({ ...local, monto: v })}
            onBlur={commit}
            onKeyDown={handleKey}
            style={{ ...inputBase, paddingLeft: 24, fontWeight: 700, textAlign: "right", paddingRight: 10 }}
          />
        </div>
        {isDirty && (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid #10B981`, background: "#D1FAE5", color: "#065F46", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700 }}
          >
            ✓
          </button>
        )}
        {confirming ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#991B1B" }}>¿Seguro?</span>
            <button onClick={() => { onDelete(); setConfirming(false); }}
              aria-label="Confirmar eliminar"
              style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</button>
            <button onClick={() => setConfirming(false)}
              aria-label="Cancelar"
              style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#F3F4F6", color: "#6B7280", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textXs, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            ×
          </button>
        )}
      </div>
      {/* Línea 2: cuenta + fecha */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, paddingLeft: row.esRecurrente ? 24 : 0 }}>
        <AccountSelect value={local.cuentaId} onChange={(v) => editAndCommit({ ...local, cuentaId: v })} cuentas={cuentas} required />
        <input
          aria-label="Fecha"
          type="date"
          value={local.fecha || todayStr}
          onChange={(e) => editAndCommit({ ...local, fecha: e.target.value })}
          style={{ ...inputBase, width: 130, fontSize: 11, padding: "5px 6px", color: P.textSm }}
        />
      </div>
    </div>
  );
}
